import { MAX_PARTY_POSITIONS, type MinimapSpot } from "odyssey-shared";
import { copyLiveParty, createLiveParty, partySpotSource, rememberLiveParty, type LiveParty, type PartySeat } from "./live-spots.ts";

/**
 * A spot the pool rewrites in place.
 */
interface PooledSpot {
  handle: number;
  x: number;
  y: number;
  name: string;
  place: number;
}

/**
 * Two lists of preallocated spots, one shown and one written; a frame that moves a mark writes the other and swaps, so
 * walking allocates nothing and a changed list is a different array.
 */
class SpotPool {
  private readonly lists: [PooledSpot[], PooledSpot[]] = [[], []];
  private readonly spots: [PooledSpot[], PooledSpot[]];
  private shown = 0;

  constructor() {
    const make = (): PooledSpot[] => Array.from({ length: MAX_PARTY_POSITIONS }, () => ({ handle: 0, x: 0, y: 0, name: "", place: 0 }));
    this.spots = [make(), make()];
  }

  /**
   * The list not shown, emptied for writing.
   *
   * @returns The list.
   */
  public begin(): PooledSpot[] {
    const list = this.lists[1 - this.shown] as PooledSpot[];
    list.length = 0;
    return list;
  }

  /**
   * Writes a spot at the end of the list being written.
   *
   * @param list The list from `begin`.
   * @param from The feed's spot, for its handle, name and place.
   * @param x Tiles across.
   * @param y Tiles down.
   */
  public put(list: PooledSpot[], from: MinimapSpot, x: number, y: number): void {
    const spot = (this.spots[1 - this.shown] as PooledSpot[])[list.length] as PooledSpot;
    spot.handle = from.handle;
    spot.name = from.name;
    spot.place = from.place;
    spot.x = x;
    spot.y = y;
    list.push(spot);
  }

  /**
   * Shows the list written.
   */
  public swap(): void {
    this.shown = 1 - this.shown;
  }
}

/**
 * Whether two lists of party members stand in the same places with the same names and colours.
 *
 * @param a One list.
 * @param b The other.
 * @returns True when each member matches its counterpart.
 */
export function sameSpots(a: readonly MinimapSpot[], b: readonly MinimapSpot[]): boolean {
  return a.length === b.length && a.every((spot, index) => {
    const other = b[index] as MinimapSpot;
    return spot.handle === other.handle && spot.x === other.x && spot.y === other.y && spot.name === other.name && spot.place === other.place;
  });
}

/**
 * The party's marks of the minimap and the Map's Nearby field (P2.12-T35, `live-spots.ts`): the members the
 * `partyPositions` feed last placed, resolved each frame against the members the scene drew and the ones it last drew.
 *
 * @example
 * const marks = new PartyMarks();
 * marks.setFed(spots, seats);
 * if (marks.setLive(live, player)) redraw(marks.spots());
 */
export class PartyMarks {
  private fed: readonly MinimapSpot[] = [];
  private seatsHeld: readonly PartySeat[] = [];
  private live: LiveParty = createLiveParty();
  private readonly seen = createLiveParty();
  private readonly pool = new SpotPool();
  private shown: readonly MinimapSpot[] = [];

  /**
   * Takes the feed's members and their names. A feed that places every member where it did before, as a roster
   * change does, keeps what was remembered of the drawn members; one that moves any forgets the members no longer drawn.
   *
   * @param spots The members as the feed placed them.
   * @param seats Their names.
   * @param self The player's position in tiles.
   * @returns True when a mark moved, came or went.
   */
  public setFed(spots: readonly MinimapSpot[], seats: readonly PartySeat[], self: { readonly x: number; readonly y: number }): boolean {
    if (!sameSpots(this.fed, spots)) {
      copyLiveParty(this.seen, this.live);
    }
    this.fed = spots;
    this.seatsHeld = seats;
    return this.resolve(self);
  }

  /**
   * Takes this frame's drawn members.
   *
   * @param live The members drawn, the same buffer every frame.
   * @param self The player's position in tiles.
   * @returns True when a mark moved, came or went.
   */
  public setLive(live: LiveParty, self: { readonly x: number; readonly y: number }): boolean {
    this.live = live;
    rememberLiveParty(this.seen, live);
    return this.fed.length > 0 || this.shown.length > 0 ? this.resolve(self) : false;
  }

  /**
   * Takes the player's new position, which decides whether a feed position lies inside vision.
   *
   * @param self The player's position in tiles.
   * @returns True when a mark came or went.
   */
  public moveSelf(self: { readonly x: number; readonly y: number }): boolean {
    return this.fed.length > 0 ? this.resolve(self) : false;
  }

  /**
   * Whether the feed names any member on the map.
   *
   * @returns True with a member.
   */
  public held(): boolean {
    return this.fed.length > 0;
  }

  /**
   * The names of the members on the map.
   *
   * @returns The seats as last given.
   */
  public seats(): readonly PartySeat[] {
    return this.seatsHeld;
  }

  /**
   * The marks.
   *
   * @returns The spots; the same array until a mark moves.
   */
  public spots(): readonly MinimapSpot[] {
    return this.shown;
  }

  /**
   * Places the marks in the list not shown and shows it when it differs.
   *
   * @param self The player's position in tiles.
   * @returns True when it differed.
   */
  private resolve(self: { readonly x: number; readonly y: number }): boolean {
    const list = this.pool.begin();
    for (const spot of this.fed) {
      const at = partySpotSource(spot, this.seen, self);
      if (at === -1) {
        this.pool.put(list, spot, spot.x, spot.y);
      } else if (at >= 0) {
        this.pool.put(list, spot, this.seen.x[at] as number, this.seen.y[at] as number);
      }
    }
    if (sameSpots(this.shown, list)) {
      return false;
    }
    this.pool.swap();
    this.shown = list;
    return true;
  }
}
